import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { formatExchangeNameUpper as formatExchangeName } from '../utils/exchangeDisplay';
import './Dashboard.css';

const Dashboard = () => {
  const { user, updateUser } = useAuth();
  const [wallets, setWallets] = useState([]);
  const [allPositions, setAllPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [apiKeys, setApiKeys] = useState([]);
  const [closing, setClosing] = useState(false);
  const [selectedExchange, setSelectedExchange] = useState('all');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // APIキー状態確認
      const apiKeyRes = await api.get('/apikey');
      const keys = apiKeyRes.data.apiKeys || [];
      setApiKeys(keys);

      if (keys.length > 0) {
        // 全取引所のウォレット・ポジション情報取得
        const [walletRes, positionsRes] = await Promise.all([
          api.get('/trading/wallet'),
          api.get('/trading/positions')
        ]);
        setWallets(walletRes.data.wallets || []);
        setAllPositions(positionsRes.data.positions || []);
      }
    } catch (error) {
      console.error('Fetch data error:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleTrading = async () => {
    try {
      const newStatus = !user.tradingEnabled;
      const response = await api.post('/trading/toggle', { enabled: newStatus });
      updateUser({ tradingEnabled: newStatus });
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.message || 'エラーが発生しました');
    }
  };

  const closeAllPositions = async (exchange) => {
    if (!window.confirm(`${exchange}の全てのポジションを決済しますか？`)) return;

    setClosing(true);
    try {
      // Bitget は Transfer 経由型で別エンドポイント (delta-neutral/close) が必要。
      // それ以外は close-all (perp close + spot dust 売却) で完結。
      // 両方を呼ぶ設計: close-all は Bitget をサーバ側でスキップする。
      const calls = [api.post('/trading/close-all', { exchange })];
      if (exchange === 'bitget') {
        // 該当 exchange のポジションから coin を抽出 (BTCUSD → BTC)
        const exchangePos = allPositions.find(p => p.exchange === exchange);
        const perpPos = (exchangePos?.positions || []).find(p => p.market === 'perp' && parseFloat(p.size) > 0);
        const coin = (perpPos?.symbol || '').replace(/USD[TC]?$/, '');
        if (!coin) {
          toast.error('Bitget の対象 coin が判定できません (active position なし)');
          setClosing(false);
          return;
        }
        calls.push(api.post('/trading/delta-neutral/close', { exchange, coin }));
      }
      await Promise.all(calls);
      toast.success(`${exchange}の全ポジションを決済しました`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || '決済に失敗しました');
    } finally {
      setClosing(false);
    }
  };

  const formatNumber = (num, decimals = 8) => {
    if (num === undefined || num === null || isNaN(num)) return '-';
    return parseFloat(num).toFixed(decimals);
  };

  // USD 表記 ($-12.34 ではなく -$12.34 にする)
  const formatUsd = (num) => {
    if (num === undefined || num === null || isNaN(num)) return '-';
    const n = parseFloat(num);
    const sign = n < 0 ? '-' : '';
    return `${sign}$${Math.abs(n).toFixed(2)}`;
  };

  // フィルタリングされたデータ
  const filteredWallets = selectedExchange === 'all'
    ? wallets
    : wallets.filter(w => w.exchange === selectedExchange);

  const filteredPositions = selectedExchange === 'all'
    ? allPositions
    : allPositions.filter(p => p.exchange === selectedExchange);

  // ポジションがある取引所のリスト
  const exchangesWithPositions = [...new Set(allPositions.filter(p => p.positions?.length > 0).map(p => p.exchange))];

  if (loading) {
    return <div className="page-loading">読み込み中...</div>;
  }

  return (
    <div className="dashboard">
      <div className="page-header">
        <h1>ダッシュボード</h1>
        <div className="header-actions">
          {apiKeys.length > 0 && (
            <>
              <select
                className="exchange-filter"
                value={selectedExchange}
                onChange={(e) => setSelectedExchange(e.target.value)}
              >
                <option value="all">全取引所</option>
                {apiKeys.map(key => (
                  <option key={key.exchange} value={key.exchange}>
                    {formatExchangeName(key.exchange)}
                  </option>
                ))}
              </select>
              <button
                className={`trading-toggle ${user.tradingEnabled ? 'active' : ''}`}
                onClick={toggleTrading}
              >
                自動取引: {user.tradingEnabled ? 'ON' : 'OFF'}
              </button>
            </>
          )}
        </div>
      </div>

      {apiKeys.length === 0 ? (
        <div className="no-apikey-notice">
          <div className="notice-icon">🔑</div>
          <h2>APIキーを登録してください</h2>
          <p>取引を開始するには、取引所のAPIキーを登録する必要があります。</p>
          <a href="/account" className="notice-btn">アカウント設定へ</a>
        </div>
      ) : (
        <>
          {/* ウォレット残高（取引所横断で一つのグリッド） */}
          <section className="dashboard-section">
            <h2 className="section-title">保有資産</h2>
            {filteredWallets.some(ew => ew.error) && (
              <div className="wallet-errors">
                {filteredWallets.filter(ew => ew.error).map(ew => (
                  <div key={`err-${ew.exchange}`} className="exchange-header">
                    <span className="exchange-name">{formatExchangeName(ew.exchange)}</span>
                    {ew.isTestnet && <span className="testnet-badge">テストネット</span>}
                    <span className="error-badge">{ew.error}</span>
                  </div>
                ))}
              </div>
            )}
            {(() => {
              const walletLabel = (w) => w === 'spot' ? 'Spot' : w === 'perp' ? 'Perp' : null;
              const allCards = filteredWallets
                .filter(ew => !ew.error)
                .flatMap(ew => (ew.wallet || []).map(coin => ({ ew, coin })));
              if (allCards.length === 0) {
                return <div className="empty-message">資産データがありません</div>;
              }
              return (
                <div className="wallet-grid">
                  {allCards.map(({ ew, coin }) => (
                    <div className="wallet-card" key={`${ew.exchange}-${coin.currency}-${coin.wallet || ''}`}>
                      <div className="wallet-header">
                        <span className="coin-symbol">
                          {coin.currency}
                          {walletLabel(coin.wallet) && (
                            <span className="wallet-type-badge"> ({walletLabel(coin.wallet)})</span>
                          )}
                        </span>
                        <span className="exchange-tag">
                          {formatExchangeName(ew.exchange)}
                          {ew.isTestnet && <span className="testnet-badge-inline">TESTNET</span>}
                        </span>
                      </div>
                      <div className="wallet-body">
                        <div className="wallet-row">
                          <span className="label">総残高</span>
                          <span className="value">{formatUsd(coin.walletBalanceUsd)}</span>
                        </div>
                        <div className="wallet-row">
                          <span className="label">利用可能</span>
                          <span className="value">{formatUsd(coin.availableBalanceUsd)}</span>
                        </div>
                        <div className="wallet-row">
                          <span className="label">使用中証拠金</span>
                          <span className="value">{formatUsd(coin.usedMarginUsd)}</span>
                        </div>
                        <div className="wallet-row">
                          <span className="label">未実現損益</span>
                          <span className={`value ${coin.unrealizedPnlUsd >= 0 ? 'profit' : 'loss'}`}>
                            {formatUsd(coin.unrealizedPnlUsd)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
            <div className="wallet-note">* USD換算で1ドル未満の資産は表示されません</div>
          </section>

          {/* ポジション（取引所ごと） */}
          <section className="dashboard-section">
            <h2 className="section-title">建玉状況</h2>
            {filteredPositions.some(p => p.positions?.length > 0) ? (
              filteredPositions.filter(p => p.positions?.length > 0).map((exchangePos) => (
                <div key={exchangePos.exchange} className="exchange-positions-section">
                  <div className="exchange-header">
                    <div className="exchange-header-left">
                      <span className="exchange-name">{formatExchangeName(exchangePos.exchange)}</span>
                      {exchangePos.isTestnet && <span className="testnet-badge">テストネット</span>}
                    </div>
                    <button
                      className="close-all-btn"
                      onClick={() => closeAllPositions(exchangePos.exchange)}
                      disabled={closing}
                    >
                      {closing ? '決済中...' : '全決済'}
                    </button>
                  </div>
                  <div className="positions-table-wrapper">
                    <table className="positions-table">
                      <thead>
                        <tr>
                          <th>シンボル</th>
                          <th>市場</th>
                          <th>方向</th>
                          <th>数量</th>
                          <th>参入価格</th>
                          <th>現在価格</th>
                          <th>USD換算価値</th>
                          <th>未実現損益</th>
                          <th>レバレッジ</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(exchangePos.positions || []).map((pos, idx) => (
                          <tr key={idx}>
                            <td className="symbol">{pos.symbol}</td>
                            <td>{pos.market === 'spot' ? '現物' : '先物'}</td>
                            <td className={pos.side === 'Buy' || pos.side === 'LONG' ? 'buy' : 'sell'}>
                              {pos.side === 'Buy' || pos.side === 'LONG' ? 'ロング' : 'ショート'}
                            </td>
                            <td>{pos.size}</td>
                            <td>{formatNumber(pos.entryPrice, 2)}</td>
                            <td>{pos.markPrice > 0 ? formatNumber(pos.markPrice, 2) : '-'}</td>
                            <td>{pos.positionValue > 0 ? formatNumber(pos.positionValue, 2) : '-'}</td>
                            <td className={pos.unrealizedPnl >= 0 ? 'profit' : 'loss'}>
                              {pos.market === 'spot' ? '-' : formatNumber(pos.unrealizedPnl, 8)}
                            </td>
                            <td>{pos.market === 'spot' ? '-' : `${pos.leverage}x`}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))
            ) : (
              <div className="empty-message">現在ポジションはありません</div>
            )}
          </section>

          {/* システム情報 */}
          <section className="dashboard-section">
            <h2 className="section-title">システム情報</h2>
            <div className="info-grid">
              <div className="info-card">
                <span className="info-label">登録取引所</span>
                <span className="info-value">{apiKeys.map(k => formatExchangeName(k.exchange)).join(', ')}</span>
              </div>
              <div className="info-card">
                <span className="info-label">自動取引</span>
                <span className={`info-value ${user.tradingEnabled ? 'active' : 'inactive'}`}>
                  {user.tradingEnabled ? '稼働中' : '停止中'}
                </span>
              </div>
              <div className="info-card">
                <span className="info-label">レバレッジ</span>
                <span className="info-value">1倍（固定）</span>
              </div>
            </div>
          </section>

          {/* サービス利用の流れ */}
          <section className="dashboard-section">
            <h2 className="section-title">サービス利用の流れ</h2>
            <div className="usage-flow">
              <p>デルタニュートラル (DN) ポジションをエントリーするには、以下の準備が必要です:</p>
              <ol className="usage-steps">
                <li>
                  <strong>取引所に入金</strong>
                  <span className="usage-step-desc">対応取引所 (Bitget / Hyperliquid / Aster) の口座に運用資金を入金してください。取引所ごとの推奨通貨と振分けは「通貨設定」画面の各取引所タブで確認できます。</span>
                </li>
                <li>
                  <strong>API キーの発行</strong>
                  <span className="usage-step-desc">取引所の管理画面で API キーを発行し、本サービスの「アカウント」画面で登録してください。必要な権限は取引 (Trade) のみです。</span>
                </li>
                <li>
                  <strong>通貨設定</strong>
                  <span className="usage-step-desc">「通貨設定」画面で取引所ごとに DN 運用するペア (BTC / ETH / BNB 等) を 1 つ選択してください。1 取引所 1 ペアまで有効化できます。</span>
                </li>
                <li>
                  <strong>自動取引 ON</strong>
                  <span className="usage-step-desc">ダッシュボード右上の「自動取引」ボタンを ON にすると、毎時 00 分にエントリー条件 (FR &gt; 0 かつ 直近 7 日平均 FR &gt; 0) を判定し、満たした時だけ自動でエントリーします。</span>
                </li>
              </ol>
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default Dashboard;
